/* eslint-disable parog-web/no-glint-expect-error-directive */
import type { TOC } from '@ember/component/template-only';
import { concat, fn, get, hash } from '@ember/helper';
import Component from '@glimmer/component';
import type { ComponentLike, WithBoundArgs as WBA } from '@glint/template';
import type { EmberChangeset } from 'ember-changeset';
import t from 'ember-intl/helpers/t';
import { or } from 'ember-truth-helpers';
import EditionForm from 'parog-web/components/comorbidity/edition-form';
import DatePicker from 'parog-web/components/date-picker';
import { FurField } from 'parog-web/components/enrollment/fields';
import Provider, {
  type Signature as ProviderSig
} from 'parog-web/components/forc/provider';
import AccordionBlock from 'parog-web/components/medical-history/accordion-block';
import TreatmentsFields from 'parog-web/components/medical-history/treatments/treatments-fields';
import type Patient from 'parog-web/models/patient';
import type TherapeuticalArea from 'parog-web/models/therapeutical-area';
import type Vaccine from 'parog-web/models/vaccine';
import AlignedFieldsRadioGroup from 'ui-kit/components/aligned-fields-radio-group';
import type { EnrollmentChangeset } from './abstract/base-edition-form';

type ProviderYieldedForm = ProviderSig['Blocks']['content'][0];

interface Signature {
  Element: HTMLDivElement;
  Args: {
    setVaccinationDateProperty?: unknown;
    setDoneProperty?: unknown;
    changeset?: unknown;
    shouldShowDateLabel?: unknown;
    comorbiditiesChangeset?: unknown;
    patient: Patient;
    forcComponent: {
      treatmentsChangeset: EmberChangeset;
      patient: Patient;
      comorbiditiesChangeset: EmberChangeset;
      shouldShowDateLabel: boolean;
      setVaccinationDateProperty: (vaccine: Vaccine) => void;
      setDoneProperty: (vaccine: Vaccine) => void;
      changeset: EmberChangeset;
      enrollmentChangeset: EnrollmentChangeset;
    };
  };
  Blocks: {
    default?: [
      {
        form: ProviderYieldedForm;
        patient: Patient;
        FurSection: WBA<typeof FurSection, 'patient' | 'forcComponent'>;
        FurDetailsSection: WBA<
          typeof FurDetailsSection,
          'patient' | 'form' | 'forcComponent'
        >;
        ComorbiditiesAllergiesSection: WBA<
          typeof ComorbiditiesAllergiesSection,
          'patient' | 'forcComponent'
        >;
        PathologySection: WBA<typeof PathologySection, 'patient' | 'form'>;
        VaccinesSection: WBA<
          typeof VaccinesSection,
          'patient' | 'form' | 'vaccines' | 'forcComponent'
        >;
        CommentsSection: WBA<typeof CommentsSection, 'patient' | 'form'>;
      }
    ];
  };
}

interface FurSectionSignature {
  Args: {
    patient: Patient;
    forcComponent: Signature['Args']['forcComponent'];
  };
}

class FurSection extends Component<FurSectionSignature> {
  get shouldDisplayFurSelection(): boolean {
    // display FuR selection only when the patient's therapeutical has several follow up reason options
    return (this.therapeuticalArea?.followUpReasons?.length ?? 0) > 1;
  }

  get therapeuticalArea(): TherapeuticalArea | null {
    return this.args.patient.followUpReasonRecord?.therapeuticalArea ?? null;
  }

  <template>
    <AccordionBlock @title='Patient suivi pour' @isActive={{true}}>
      <div class='field' data-test-patient-fur>
        {{t (concat 'therapeutical-area.' @patient.therapeuticalArea.id)}}
        {{#unless @patient.isGeneric}}
          :
          {{t
            (concat
              'follow-up-reason.'
              @forcComponent.enrollmentChangeset.followUpReason
            )
          }}

        {{/unless}}
      </div>
      {{#if this.shouldDisplayFurSelection}}
        <FurField
          @changeset={{@forcComponent.enrollmentChangeset}}
          @therapeuticalArea={{this.therapeuticalArea}}
        />
      {{/if}}

    </AccordionBlock>
  </template>
}

const FurDetailsSection = <template>
  {{#if @diagnostic}}
    <AccordionBlock
      @title={{or @title 'Détails du suivi'}}
      @isActive={{true}}
      class='pt-[1em]'
    >
      {{#if @patient.healthCentre.preTransplant}}
        <div class='field'>
          <@form.Checkbox
            @labelName='Dossier pré greffe'
            @payloadKey='preTransplantFolder'
            @isReadOnly={{true}}
            @disabled={{true}}
          />
        </div>
      {{/if}}

      <@diagnostic @form={{@form}} @patient={{@patient}} />

      {{! TREATMENTS SECTION }}
      <TreatmentsFields @patient={{@patient}} as |TreatmentsSection|>
        <TreatmentsSection
          @changeset={{@forcComponent.treatmentsChangeset}}
          @treatmentPrescriptionChangedJournalEvents={{@forcComponent.patient.treatmentPrescriptionChangedJournalEvents}}
        />
      </TreatmentsFields>
    </AccordionBlock>
  {{/if}}
  {{#if @dialysis}}
    <AccordionBlock
      @title={{or @title 'Dialyse'}}
      @isActive={{true}}
      class='pt-[1em]'
    >

      <@dialysis @form={{@form}} @patient={{@patient}} />

    </AccordionBlock>
  {{/if}}
</template> satisfies TOC<{
  Args: {
    title?: string;
    patient: Patient;
    form: ProviderYieldedForm;
    forcComponent: Signature['Args']['forcComponent'];
    diagnostic?: ComponentLike<{ patient: Patient; form: ProviderYieldedForm }>;
    dialysis?: ComponentLike<{ patient: Patient; form: ProviderYieldedForm }>;
  };
}>;

const ComorbiditiesAllergiesSection = <template>
  <AccordionBlock
    @title='Comorbidités et allergies'
    @isActive={{true}}
    class='pt-[1em]'
  >
    <EditionForm
      @patient={{@patient}}
      @changeset={{@forcComponent.comorbiditiesChangeset}}
    />
  </AccordionBlock>
</template> satisfies TOC<{
  Args: { patient: Patient; forcComponent: Signature['Args']['forcComponent'] };
}>;

const PathologySection = <template>
  {{#if @pathology}}
    <AccordionBlock @title='Pathologie' @isActive={{false}} class='pt-[1em]'>
      <@pathology @form={{@form}} @patient={{@patient}} />
    </AccordionBlock>
  {{/if}}
</template> satisfies TOC<{
  Args: {
    patient: Patient;
    form: ProviderYieldedForm;
    pathology: ComponentLike<{ patient: Patient; form: ProviderYieldedForm }>;
  };
}>;

const VaccinesSection = <template>
  <AccordionBlock @title='Suivi vaccinal' @isActive={{false}} class='pt-[1em]'>
    <div class='fields [.ui.form_&.fields]:mb-0' data-test-section='vaccine'>
      <div class='nine wide field'>
      </div>
      {{#if @forcComponent.shouldShowDateLabel}}
        <div class='four wide field'>
          Date de vaccination
        </div>
      {{/if}}
    </div>

    {{#each @vaccines as |vaccine|}}
      <div class='flex flex-col'>
        <div class='fields inline'>
          <div class='four wide field'>
            <label class='[.ui.form_.inline.fields_&field]:text-[#808080]'>
              {{t (concat 'vaccines.' vaccine.name)}}
            </label>
          </div>
          <div class='twelve wide field'>
            <AlignedFieldsRadioGroup
              @onChange={{fn @forcComponent.setDoneProperty vaccine}}
              @current={{vaccine.done}}
              @propertyName='done'
              as |Group|
            >
              <Group.FieldRadio @value={{true}} @label='Oui' />
              <Group.FieldRadio @value={{false}} @label='Non' />
              <Group.FieldRadio @value={{undefined}} @label='Non communiqué' />

            </AlignedFieldsRadioGroup>
          </div>

          <div class='four wide field {{unless vaccine.done "invisible"}}'>
            <DatePicker
              @selected={{vaccine.vaccinationDate}}
              @onChange={{fn @forcComponent.setVaccinationDateProperty vaccine}}
            />
          </div>

        </div>

        {{#let
          (get
            @forcComponent.changeset.error
            (concat 'vaccines.' vaccine.name '.vaccinationDate')
          )
          as |err|
        }}
          {{#if err}}
            <div class='ui error text text-right text-[#DB2828]'>
              {{! @glint-expect-error ignoring type of validation error}}
              {{err.validation}}
            </div>
          {{/if}}
        {{/let}}

      </div>
    {{/each}}
  </AccordionBlock>
</template> satisfies TOC<{
  Args: {
    patient: Patient;
    form: ProviderYieldedForm;
    forcComponent: Signature['Args']['forcComponent'];
    vaccines: Vaccine[];
  };
}>;

const CommentsSection = <template>
  <AccordionBlock
    @title='Autres informations'
    @isActive={{false}}
    class='pt-[1em]'
  >
    <div class='field'>
      <@form.TextArea @propertyName='comments' />
    </div>
  </AccordionBlock>
</template> satisfies TOC<{
  Args: {
    patient: Patient;
    form: ProviderYieldedForm;
  };
}>;

export default class MedicalHistoryBaseEditionForm extends Component<Signature> {
  get vaccines(): Vaccine[] {
    return this.args.forcComponent.changeset.vaccines as unknown as Vaccine[];
  }

  <template>
    {{! @glint-expect-error }}
    <Provider @forcComponent={{@forcComponent}} @patient={{@patient}}>
      <:content as |Form|>
        {{#if @patient.isEnrolled}}
          {{yield
            (hash
              form=Form
              patient=@patient
              FurSection=(component
                FurSection patient=@patient forcComponent=@forcComponent
              )
              FurDetailsSection=(component
                FurDetailsSection
                patient=@patient
                form=Form
                forcComponent=@forcComponent
              )
              ComorbiditiesAllergiesSection=(component
                ComorbiditiesAllergiesSection
                patient=@patient
                forcComponent=@forcComponent
              )
              PathologySection=(component
                PathologySection patient=@patient form=Form
              )
              VaccinesSection=(component
                VaccinesSection
                patient=@patient
                form=Form
                forcComponent=@forcComponent
                vaccines=this.vaccines
              )
              CommentsSection=(component
                CommentsSection patient=@patient form=Form
              )
            )
          }}
        {{else}}
          <div class='field'>
            <div class='ui message'>
              Ce patient est inscrit, mais pas encore inclus.
            </div>
          </div>
        {{/if}}

        <footer class='m-[1em] mx-0'>
          <p
            class='ui placeholder-label before:text-pink before:font-bold before:content-[*]'
          >Champs obligatoires</p>
        </footer>
      </:content>
    </Provider>
  </template>
}
